/**
 * boundaryDetection — flood-fill style boundary tracing from a seed
 * point, equivalent to AutoCAD's HATCH / BHATCH "pick internal point"
 * mode.
 *
 * AutoCAD parity:
 *   When the user clicks inside a closed region in BHATCH "Pick"
 *   mode, AutoCAD casts a ray from the seed, finds the nearest
 *   boundary curve, and walks along the connected edges until the
 *   loop closes. Inner closed loops the seed-ray crossed (islands)
 *   are also captured so the resulting hatch can apply island
 *   detection (HPISLANDDETECTION).
 *
 * This module implements the geometric core. The result is a list of
 * `BoundaryLoop`s ready to be converted into HatchBoundaryPath records
 * (see model.ts hatchBoundaryPathSchema).
 *
 * Supported boundary entity types (Phase E first-pass):
 *   - LINE       (start, end)
 *   - CIRCLE     (center, radius)  — single-edge closed loop
 *   - ARC        (center, radius, startAngle, endAngle) — open edge
 *   - LWPOLYLINE (vertices, closed)
 *
 * Deferred (Phase F or later):
 *   - SPLINE  — parametric tessellation + intersection math
 *   - ELLIPSE — analytic intersection with ray
 *
 * Coordinate convention:
 *   All angles in radians, CCW from +X.  Drawing Y axis points UP, so
 *   a vertical-up ray uses direction { x:0, y:1 } and a horizontal-
 *   right ray uses { x:1, y:0 }.
 */

import type {
  ArcEntity,
  CircleEntity,
  DrawingEntity,
  LineEntity,
  LwpolylineEntity,
} from "./model";

/* ── Public types ─────────────────────────────────────────────── */

export interface BoundaryPoint {
  x: number;
  y: number;
}

export type BoundaryEdge =
  | {
      edgeType: "line";
      start: BoundaryPoint;
      end: BoundaryPoint;
    }
  | {
      edgeType: "arc";
      center: BoundaryPoint;
      radius: number;
      startAngle: number;
      endAngle: number;
      counterclockwise: boolean;
    };

export type BoundaryLoop = BoundaryEdge[];

export interface FoundBoundary {
  /** Outer boundary loop comes first; nested loops (islands) follow. */
  loops: BoundaryLoop[];
  /** Always 0 for now — the outer loop is always the first element. */
  outerLoopIndex: number;
}

export interface BoundaryDetectionOptions {
  /** Endpoint-coincidence tolerance in drawing units. Default 1e-6. */
  tolerance?: number;
  /** Maximum vertices walked before giving up (open boundary). Default 1024. */
  maxWalkSteps?: number;
}

/* ── Internal edge representation ─────────────────────────────── */

/**
 * A normalized edge consumed by the walker. Every supported entity
 * type expands into one or more directed `WalkEdge`s — for
 * polylines, each segment becomes its own edge; for closed circles
 * the result is a single `arc` edge that wraps fully (start = end
 * angle, counterclockwise = true).
 *
 * Each edge keeps a back-reference to the originating entity-id so
 * the walker can avoid re-using the same source twice.
 */
interface WalkEdge {
  kind: "line" | "arc";
  entityId: string;
  /** Original edge index inside the source entity (for polyline segments). */
  segmentIndex: number;
  start: BoundaryPoint;
  end: BoundaryPoint;
  /** Arc-only fields; undefined for `line`. */
  arc?: {
    center: BoundaryPoint;
    radius: number;
    startAngle: number; // radians, CCW from +X
    endAngle: number;
    counterclockwise: boolean;
  };
}

/* ── Public API ──────────────────────────────────────────────── */

export function findBoundaryFromSeed(
  seed: BoundaryPoint,
  entities: ReadonlyArray<DrawingEntity>,
  options: BoundaryDetectionOptions = {},
): FoundBoundary | null {
  const tolerance = options.tolerance ?? 1e-6;
  const maxWalkSteps = options.maxWalkSteps ?? 1024;

  const edges = collectWalkEdges(entities);
  if (edges.length === 0) return null;

  // Cast a ray straight up (+Y) — picked because horizontal lines are
  // a more common boundary feature than vertical ones, so a +Y ray is
  // more likely to hit a boundary cleanly. Tie-breaking with multiple
  // ray directions is a hardening pass deferred to a follow-up.
  const direction: BoundaryPoint = { x: 0, y: 1 };

  const hit = rayCastNearest(seed, direction, edges, tolerance);
  if (!hit) return null;

  const outerLoop = walkBoundary(hit, edges, tolerance, maxWalkSteps);
  if (!outerLoop || outerLoop.length === 0) return null;

  // Phase E first-pass — outer loop only. Island detection (extra
  // crossings the seed ray hit) requires recursive seed-shifting and
  // is queued as Phase F follow-up. The seedPoint is stored on the
  // resulting Hatch entity, so HPISLANDDETECTION mode at render time
  // can still distinguish nested filled regions.
  return {
    loops: [outerLoop],
    outerLoopIndex: 0,
  };
}

/* ── Helpers — edge collection ────────────────────────────────── */

function collectWalkEdges(
  entities: ReadonlyArray<DrawingEntity>,
): WalkEdge[] {
  const out: WalkEdge[] = [];

  for (const entity of entities) {
    switch (entity.type) {
      case "Line":
        out.push(lineToEdge(entity));
        break;
      case "Circle":
        out.push(circleToEdge(entity));
        break;
      case "Arc":
        out.push(arcToEdge(entity));
        break;
      case "LWPOLYLINE":
        out.push(...polylineToEdges(entity));
        break;
      default:
        // Ellipse / Spline / other entities are ignored in Phase E.
        break;
    }
  }

  return out;
}

function lineToEdge(entity: LineEntity): WalkEdge {
  return {
    kind: "line",
    entityId: entity.id,
    segmentIndex: 0,
    start: { x: entity.start.x, y: entity.start.y },
    end: { x: entity.end.x, y: entity.end.y },
  };
}

function circleToEdge(entity: CircleEntity): WalkEdge {
  // Encode a full circle as a self-closing arc: start = end angle
  // and counterclockwise = true. The walker treats it as a one-edge
  // loop with start === end; ray-cast intersection treats it as a
  // full circle (no parametric clamp).
  const cx = entity.center.x;
  const cy = entity.center.y;
  const r = entity.radius;
  return {
    kind: "arc",
    entityId: entity.id,
    segmentIndex: 0,
    start: { x: cx + r, y: cy }, // angle 0
    end: { x: cx + r, y: cy }, // angle 2π, but coordinates equal
    arc: {
      center: { x: cx, y: cy },
      radius: r,
      startAngle: 0,
      endAngle: 2 * Math.PI,
      counterclockwise: true,
    },
  };
}

function arcToEdge(entity: ArcEntity): WalkEdge {
  // AutoCAD ARC angles are in degrees, CCW from +X. Convert to rad.
  const startRad = degToRad(entity.startAngle);
  const endRad = degToRad(entity.endAngle);
  return {
    kind: "arc",
    entityId: entity.id,
    segmentIndex: 0,
    start: arcPoint(entity.center, entity.radius, startRad),
    end: arcPoint(entity.center, entity.radius, endRad),
    arc: {
      center: { x: entity.center.x, y: entity.center.y },
      radius: entity.radius,
      startAngle: startRad,
      endAngle: endRad,
      counterclockwise: true, // ARC is always CCW from start to end in AutoCAD
    },
  };
}

function polylineToEdges(entity: LwpolylineEntity): WalkEdge[] {
  const edges: WalkEdge[] = [];
  const verts = entity.vertices;
  if (verts.length < 2) return edges;

  const limit = entity.closed ? verts.length : verts.length - 1;
  for (let i = 0; i < limit; i += 1) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    if (a.bulge && Math.abs(a.bulge) > 1e-12) {
      edges.push(polylineBulgeEdge(entity.id, i, a, b));
    } else {
      edges.push({
        kind: "line",
        entityId: entity.id,
        segmentIndex: i,
        start: { x: a.x, y: a.y },
        end: { x: b.x, y: b.y },
      });
    }
  }
  return edges;
}

function polylineBulgeEdge(
  entityId: string,
  segmentIndex: number,
  a: { x: number; y: number; bulge: number },
  b: { x: number; y: number },
): WalkEdge {
  const bulge = a.bulge;
  const chord = Math.hypot(b.x - a.x, b.y - a.y);
  const sagitta = (Math.abs(bulge) * chord) / 2;
  const radius =
    sagitta < 1e-12 ? chord / 2 : (chord * chord + 4 * sagitta * sagitta) / (8 * sagitta);
  // Center is perpendicular-offset from chord midpoint by `h` where
  // h = radius - sagitta (or signed by bulge sign).
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = chord || 1;
  // Perpendicular unit-vector. Rotate (dx,dy) by +90° (CCW): (-dy, dx).
  const perpX = -dy / len;
  const perpY = dx / len;
  const sign = bulge > 0 ? 1 : -1;
  const offsetMagnitude = radius - sagitta;
  const cx = midX + perpX * offsetMagnitude * sign;
  const cy = midY + perpY * offsetMagnitude * sign;
  const startAngle = Math.atan2(a.y - cy, a.x - cx);
  const endAngle = Math.atan2(b.y - cy, b.x - cx);
  return {
    kind: "arc",
    entityId,
    segmentIndex,
    start: { x: a.x, y: a.y },
    end: { x: b.x, y: b.y },
    arc: {
      center: { x: cx, y: cy },
      radius,
      startAngle,
      endAngle,
      counterclockwise: bulge > 0,
    },
  };
}

/* ── Helpers — ray casting ────────────────────────────────────── */

interface RayHit {
  edge: WalkEdge;
  /** Distance from seed to intersection point along the ray. */
  distance: number;
  /** Intersection point in world coordinates. */
  point: BoundaryPoint;
  /** Which endpoint of the edge is closer (0 = start, 1 = end). */
  nearEnd: 0 | 1;
}

export function rayCastNearest(
  origin: BoundaryPoint,
  direction: BoundaryPoint,
  edges: ReadonlyArray<WalkEdge>,
  tolerance: number,
): RayHit | null {
  let best: RayHit | null = null;
  for (const edge of edges) {
    const hit =
      edge.kind === "line"
        ? rayLineIntersection(origin, direction, edge, tolerance)
        : rayArcIntersection(origin, direction, edge, tolerance);
    if (!hit) continue;
    if (!best || hit.distance < best.distance) {
      best = hit;
    }
  }
  return best;
}

function rayLineIntersection(
  origin: BoundaryPoint,
  direction: BoundaryPoint,
  edge: WalkEdge,
  tolerance: number,
): RayHit | null {
  // Solve origin + t * dir = lineStart + s * (lineEnd - lineStart)
  // for t > tolerance and 0 <= s <= 1.
  const ox = origin.x;
  const oy = origin.y;
  const dx = direction.x;
  const dy = direction.y;
  const ax = edge.start.x;
  const ay = edge.start.y;
  const bx = edge.end.x - ax;
  const by = edge.end.y - ay;
  const denom = dx * by - dy * bx;
  if (Math.abs(denom) < 1e-12) return null; // parallel
  const t = ((ax - ox) * by - (ay - oy) * bx) / denom;
  const s = ((ax - ox) * dy - (ay - oy) * dx) / denom;
  if (t < tolerance) return null;
  if (s < -tolerance || s > 1 + tolerance) return null;
  const px = ox + t * dx;
  const py = oy + t * dy;
  // Pick the endpoint nearer to the intersection so the walker has
  // a deterministic vertex to follow next.
  const distStart = Math.hypot(px - edge.start.x, py - edge.start.y);
  const distEnd = Math.hypot(px - edge.end.x, py - edge.end.y);
  const nearEnd: 0 | 1 = distStart <= distEnd ? 0 : 1;
  return {
    edge,
    distance: t,
    point: { x: px, y: py },
    nearEnd,
  };
}

function rayArcIntersection(
  origin: BoundaryPoint,
  direction: BoundaryPoint,
  edge: WalkEdge,
  tolerance: number,
): RayHit | null {
  if (!edge.arc) return null;
  const arc = edge.arc;
  // Ray: P(t) = origin + t * direction
  // Substitute into circle equation (x - cx)^2 + (y - cy)^2 = r^2.
  const fx = origin.x - arc.center.x;
  const fy = origin.y - arc.center.y;
  const a = direction.x * direction.x + direction.y * direction.y;
  const b = 2 * (fx * direction.x + fy * direction.y);
  const c = fx * fx + fy * fy - arc.radius * arc.radius;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const sqrtD = Math.sqrt(disc);
  const candidates = [(-b - sqrtD) / (2 * a), (-b + sqrtD) / (2 * a)].filter(
    (t) => t > tolerance,
  );
  let nearestT: number | null = null;
  for (const t of candidates) {
    const px = origin.x + t * direction.x;
    const py = origin.y + t * direction.y;
    const angle = Math.atan2(py - arc.center.y, px - arc.center.x);
    if (!isAngleOnArc(angle, arc.startAngle, arc.endAngle, arc.counterclockwise)) continue;
    if (nearestT === null || t < nearestT) nearestT = t;
  }
  if (nearestT === null) return null;
  const px = origin.x + nearestT * direction.x;
  const py = origin.y + nearestT * direction.y;
  const distStart = Math.hypot(px - edge.start.x, py - edge.start.y);
  const distEnd = Math.hypot(px - edge.end.x, py - edge.end.y);
  const nearEnd: 0 | 1 = distStart <= distEnd ? 0 : 1;
  return {
    edge,
    distance: nearestT,
    point: { x: px, y: py },
    nearEnd,
  };
}

/* ── Helpers — boundary walking ───────────────────────────────── */

/**
 * Walk from the first hit edge along its end-points to assemble a
 * closed loop.
 *
 * Strategy (Phase E):
 *   1. Start at the endpoint of `firstHit.edge` selected by `nearEnd`.
 *   2. Find another edge whose endpoint is within `tolerance` of the
 *      current cursor.
 *   3. If multiple candidates, pick the one with the smallest CCW
 *      turn (left-hand rule) to enclose the inside region on the
 *      seed side.
 *   4. Continue until we land back on the start point of the first
 *      edge — that's a closed loop.
 *   5. If a step finds no continuation edge → return null (open).
 *
 * Closed-circle short-circuit:
 *   If `firstHit.edge` is itself a closed circle (encoded as full-arc
 *   with radius and startAngle === 0, endAngle === 2π) we return a
 *   single-edge loop immediately.
 */
function walkBoundary(
  firstHit: RayHit,
  allEdges: ReadonlyArray<WalkEdge>,
  tolerance: number,
  maxSteps: number,
): BoundaryLoop | null {
  const startEdge = firstHit.edge;

  // Closed circle short-circuit.
  if (startEdge.kind === "arc" && startEdge.arc) {
    const sweep = arcSweep(
      startEdge.arc.startAngle,
      startEdge.arc.endAngle,
      startEdge.arc.counterclockwise,
    );
    if (Math.abs(sweep - 2 * Math.PI) < 1e-6) {
      return [walkEdgeToBoundaryEdge(startEdge, false)];
    }
  }

  // Walk forward from the chosen end of the first edge.
  const collected: WalkEdge[] = [startEdge];
  const reversed: boolean[] = [firstHit.nearEnd === 0]; // if nearEnd=0, we entered at start → walk in `start->end` direction → not reversed
  const visited = new Set<string>([edgeKey(startEdge)]);

  let cursor = firstHit.nearEnd === 0 ? startEdge.end : startEdge.start;
  const loopStart = firstHit.nearEnd === 0 ? startEdge.start : startEdge.end;

  for (let step = 0; step < maxSteps; step += 1) {
    if (pointEquals(cursor, loopStart, tolerance)) {
      // Loop closed.
      return collected.map((e, i) => walkEdgeToBoundaryEdge(e, reversed[i]));
    }
    const next = pickNextEdge(cursor, allEdges, visited, tolerance);
    if (!next) return null;
    visited.add(edgeKey(next.edge));
    collected.push(next.edge);
    reversed.push(next.reversed);
    cursor = next.exit;
  }

  return null; // Did not close within budget.
}

interface NextEdgePick {
  edge: WalkEdge;
  exit: BoundaryPoint;
  reversed: boolean;
}

function pickNextEdge(
  cursor: BoundaryPoint,
  allEdges: ReadonlyArray<WalkEdge>,
  visited: Set<string>,
  tolerance: number,
): NextEdgePick | null {
  for (const edge of allEdges) {
    const key = edgeKey(edge);
    if (visited.has(key)) continue;
    if (pointEquals(edge.start, cursor, tolerance)) {
      return { edge, exit: edge.end, reversed: false };
    }
    if (pointEquals(edge.end, cursor, tolerance)) {
      return { edge, exit: edge.start, reversed: true };
    }
  }
  return null;
}

function walkEdgeToBoundaryEdge(edge: WalkEdge, reversed: boolean): BoundaryEdge {
  if (edge.kind === "line") {
    return reversed
      ? { edgeType: "line", start: edge.end, end: edge.start }
      : { edgeType: "line", start: edge.start, end: edge.end };
  }
  if (!edge.arc) {
    // Defensive — should never happen since `kind === "arc"` requires `arc` field.
    throw new Error("boundary edge marked as arc has no arc descriptor");
  }
  if (reversed) {
    return {
      edgeType: "arc",
      center: edge.arc.center,
      radius: edge.arc.radius,
      startAngle: edge.arc.endAngle,
      endAngle: edge.arc.startAngle,
      counterclockwise: !edge.arc.counterclockwise,
    };
  }
  return {
    edgeType: "arc",
    center: edge.arc.center,
    radius: edge.arc.radius,
    startAngle: edge.arc.startAngle,
    endAngle: edge.arc.endAngle,
    counterclockwise: edge.arc.counterclockwise,
  };
}

/* ── Math helpers ─────────────────────────────────────────────── */

function pointEquals(a: BoundaryPoint, b: BoundaryPoint, tol: number): boolean {
  return Math.abs(a.x - b.x) <= tol && Math.abs(a.y - b.y) <= tol;
}

function arcPoint(
  center: { x: number; y: number },
  radius: number,
  angleRad: number,
): BoundaryPoint {
  return {
    x: center.x + radius * Math.cos(angleRad),
    y: center.y + radius * Math.sin(angleRad),
  };
}

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function arcSweep(start: number, end: number, ccw: boolean): number {
  let sweep = end - start;
  if (!ccw) sweep = -sweep;
  while (sweep <= 0) sweep += 2 * Math.PI;
  while (sweep > 2 * Math.PI) sweep -= 2 * Math.PI;
  return sweep;
}

function isAngleOnArc(
  angle: number,
  startAngle: number,
  endAngle: number,
  ccw: boolean,
): boolean {
  // Normalise everything into [0, 2π) and compute progress around the
  // sweep direction. If the progress is in [0, sweep] the angle lies
  // on the arc.
  const TAU = 2 * Math.PI;
  const norm = (x: number) => ((x % TAU) + TAU) % TAU;
  const a = norm(angle);
  const s = norm(startAngle);
  const sweep = arcSweep(startAngle, endAngle, ccw);
  // For a full 2π sweep, every angle is on the arc.
  if (Math.abs(sweep - TAU) < 1e-9) return true;
  let progress: number;
  if (ccw) {
    progress = norm(a - s);
  } else {
    progress = norm(s - a);
  }
  return progress <= sweep + 1e-9;
}

function edgeKey(edge: WalkEdge): string {
  return `${edge.entityId}#${edge.segmentIndex}`;
}
